/**
 * routes/contacto.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Sprint: Contact + Brand Trust V1 — a real, fully translated contact page
 * (unlike /terminos, /privacidad, /cookies, /afiliados, this is NOT wrapped
 * in components/legal/LegalPage.tsx — it's an operational trust surface,
 * not a legal disclosure, so per this sprint's own Task 6 it's translated
 * in all 3 languages instead of staying Spanish-only).
 *
 * Deliberately honest and modest: no live chat, no 24/7 claim, no fake
 * response-time promise — SiftTrip is an affiliate marketplace/comparison
 * product, not the operator of any activity, and this page says so.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, MessageCircle, Phone, ArrowLeft } from "lucide-react";
import { useI18n } from "@/lib/i18n/useI18n";
import { SocialBrandIcon } from "@/components/SocialBrandIcon";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/contacto")({
  component: ContactRoute,
  head: () => ({
    meta: [
      { title: "Contacto — Sift Trip" },
      {
        name: "description",
        content: "Escríbenos por email, WhatsApp o redes sociales. Canales oficiales de contacto de SiftTrip.",
      },
    ],
  }),
});

const CONTACT_EMAIL   = "contact@siftrip.com";
const CONTACT_PHONE   = "+57 313 417 2540";
const WHATSAPP_URL    = "https://wa.me/573134172540";
const INSTAGRAM_URL   = "https://www.instagram.com/siftrip/";
const FACEBOOK_URL    = "https://www.facebook.com/profile.php?id=61591598895582";

const INK       = "oklch(0.07 0.012 258)";
const INK_MUTED = "oklch(0.38 0.016 258 / 0.58)";
const BORDER    = "oklch(0.880 0.014 70 / 0.45)";
const ACCENT    = "#E8194B";

function ContactRoute() {
  const { t } = useI18n();

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#FAFAFA" }}>
      <div className="flex-1 px-5 py-12 sm:px-8">
        <div className="mx-auto max-w-2xl">
          <Link
            to="/"
            className="mb-8 inline-flex items-center gap-1.5 text-sm font-semibold transition-opacity hover:opacity-80"
            style={{ color: ACCENT }}
          >
            <ArrowLeft size={14} aria-hidden />
            {t("contact.backHome")}
          </Link>

          <header className="mb-8 border-b pb-6" style={{ borderColor: BORDER }}>
            <p className="text-xs font-bold uppercase tracking-[0.28em]" style={{ color: ACCENT }}>
              {t("contact.channels.heading")}
            </p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: INK }}>
              {t("contact.title")}
            </h1>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: INK_MUTED }}>
              {t("contact.intro")}
            </p>
          </header>

          <div className="space-y-6">
            {/* ── Official channels — single-level rows, no nested cards ── */}
            <section className="rounded-lg p-5" style={{ background: "#FFFFFF", border: `1px solid ${BORDER}` }}>
              <div className="flex items-start gap-3 py-2">
                <Mail size={16} aria-hidden className="mt-0.5 shrink-0" style={{ color: INK_MUTED }} />
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.14em]" style={{ color: INK_MUTED }}>
                    {t("contact.email.label")}
                  </p>
                  <a
                    href="mailto:contact@siftrip.com"
                    className="font-semibold transition-opacity hover:opacity-80"
                    style={{ color: ACCENT }}
                  >
                    {CONTACT_EMAIL}
                  </a>
                </div>
              </div>

              <div className="mt-4 pt-4 flex items-start gap-3" style={{ borderTop: `1px solid ${BORDER}` }}>
                <Phone size={16} aria-hidden className="mt-0.5 shrink-0" style={{ color: INK_MUTED }} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] mb-1" style={{ color: INK_MUTED }}>
                    {t("contact.phone.label")}
                  </p>
                  <p className="font-semibold mb-2" style={{ color: INK }}>{CONTACT_PHONE}</p>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
                      style={{ background: "oklch(0.120 0.040 258)", minHeight: "36px" }}
                    >
                      <MessageCircle size={13} aria-hidden />
                      {t("contact.whatsapp.cta")}
                    </a>
                    <a
                      href="tel:+573134172540"
                      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-semibold transition-colors"
                      style={{ border: `1px solid ${BORDER}`, color: INK, minHeight: "36px" }}
                    >
                      <Phone size={12} aria-hidden />
                      {t("contact.call.cta")}
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* ── Social ──────────────────────────────────────────────── */}
            <section className="rounded-lg p-5" style={{ background: "#FFFFFF", border: `1px solid ${BORDER}` }}>
              <p className="text-xs font-bold uppercase tracking-[0.14em] mb-2" style={{ color: INK_MUTED }}>
                {t("contact.social.heading")}
              </p>
              <p className="text-sm mb-3" style={{ color: INK_MUTED }}>
                {t("contact.social.body")}
              </p>
              <div className="flex items-center gap-1 -ml-2">
                <SocialBrandIcon name="instagram" href={INSTAGRAM_URL} />
                <SocialBrandIcon name="facebook" href={FACEBOOK_URL} />
              </div>
            </section>

            {/* ── Support expectation — exact copy from this sprint's brief ── */}
            <section className="rounded-lg p-5" style={{ background: "oklch(0.978 0.010 72)", border: `1px solid ${BORDER}` }}>
              <p className="text-xs font-bold uppercase tracking-[0.14em] mb-1.5" style={{ color: INK_MUTED }}>
                {t("contact.supportExpectation.heading")}
              </p>
              <p className="text-sm leading-relaxed" style={{ color: INK }}>
                {t("contact.supportExpectation.body")}
              </p>
            </section>

            {/* ── Providers/partners ──────────────────────────────────── */}
            <section className="rounded-lg p-5" style={{ background: "#FFFFFF", border: `1px solid ${BORDER}` }}>
              <p className="text-xs font-bold uppercase tracking-[0.14em] mb-1.5" style={{ color: INK_MUTED }}>
                {t("contact.provider.heading")}
              </p>
              <p className="text-sm leading-relaxed" style={{ color: INK }}>
                {t("contact.provider.body")}
              </p>
            </section>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
